// Match the existing DECIMAL(18,4) quantity columns when adding and subtracting.
export function roundQuantity(value) {
  return Number(Number(value).toFixed(4));
}

// Keep allocation changes separate from the editable return batch until Apply succeeds.
export function sourcesOf(row) {
  return row._returnMode === 'merge'
    ? (row._sources || []).map(source => ({ ...source }))
    : (row._planTempID ? [{ planTempID: row._planTempID, consumedQuantity: Number(row.stockNumber) }] : []);
}

export function copyProduct(row) {
  return { ...row, _sources: sourcesOf(row) };
}

export function createAllocationDraft(rows) {
  return rows.map((row, index) => ({
    key: `existing-${index}`, originalIndex: index, original: copyProduct(row),
    row: copyProduct(row), active: true, touched: false
  }));
}

export function allocatedQuantity(targets, planID, excludedKey = null) {
  return roundQuantity(targets.filter(target => target.key !== excludedKey).reduce((sum, target) => sum +
    sourcesOf(target.row).filter(source => source.planTempID === planID)
      .reduce((subtotal, source) => subtotal + Number(source.consumedQuantity), 0), 0));
}

export function allocationErrors(targets, capacities, requireSources = false) {
  const errors = [];
  for (const target of targets) {
    const sources = sourcesOf(target.row);
    if (requireSources && (target.touched || target.originalIndex == null) && !sources.length) {
      errors.push({ targetKey: target.key, message: '成品必须至少关联一个来源；已有记录可移出本次分配以恢复原状' });
    }
    const seen = new Set();
    for (const source of sources) {
      const quantity = Number(source.consumedQuantity);
      let message = '';
      if (!Object.prototype.hasOwnProperty.call(capacities, source.planTempID)) message = '来源不存在或不属于当前回货单';
      else if (seen.has(source.planTempID)) message = '同一成品的来源不能重复';
      else if (!Number.isFinite(quantity) || quantity <= 0) message = '合并核销件数必须为大于0的有效数字';
      else if (target.row._returnMode === 'merge' && roundQuantity(quantity) !== quantity) message = '核销件数最多支持4位小数';
      if (message) errors.push({ planID: source.planTempID, targetKey: target.key, message });
      seen.add(source.planTempID);
    }
  }
  for (const [planID, capacity] of Object.entries(capacities)) {
    if (allocatedQuantity(targets, planID) > Number(capacity)) {
      errors.push({ planID, message: `累计分配超过本批可处理数量 ${capacity} 件` });
    }
  }
  return errors;
}

export function replaceTargetSources(targets, key, sources, capacities) {
  const target = targets.find(item => item.key === key);
  if (!target || !target.active) throw new Error('请选择有效的目标成品');
  const row = { ...target.row, _returnMode: 'merge', _sources: sources.map(source => ({ ...source })) };
  const proposed = targets.map(item => item === target ? { ...item, row, touched: true } : item);
  const error = allocationErrors(proposed, capacities)[0];
  if (error) return error;
  target.row = row;
  target.touched = true;
  return null;
}

export function addTargetAllocations(targets, key, additions, capacities) {
  const target = targets.find(item => item.key === key);
  if (!target) throw new Error('请选择目标成品');
  const sources = sourcesOf(target.row);
  for (const source of additions) {
    if (!Number.isFinite(Number(source.consumedQuantity)) || Number(source.consumedQuantity) <= 0) {
      return { planID: source.planTempID, message: '合并核销件数必须为大于0的有效数字' };
    }
    if (roundQuantity(source.consumedQuantity) !== Number(source.consumedQuantity)) return { planID: source.planTempID, message: '核销件数最多支持4位小数' };
    const existing = sources.find(item => item.planTempID === source.planTempID);
    if (existing) existing.consumedQuantity = roundQuantity(Number(existing.consumedQuantity) + Number(source.consumedQuantity));
    else sources.push({ ...source, consumedQuantity: Number(source.consumedQuantity) });
  }
  return replaceTargetSources(targets, key, sources, capacities);
}

export function removeAllocationTarget(targets, key, capacities) {
  const target = targets.find(item => item.key === key);
  if (!target) return null;
  if (target.originalIndex == null) {
    targets.splice(targets.indexOf(target), 1);
    return null;
  }
  // Existing records survive removal, including their original source reservations.
  const restored = { ...target, row: copyProduct(target.original), active: false, touched: false };
  const error = allocationErrors(targets.map(item => item === target ? restored : item), capacities)[0];
  if (error) return { ...error, message: `恢复原分配失败：${error.message}，请先调整其他成品的分配` };
  Object.assign(target, restored);
  return null;
}

export function batchSearchTokens(value) {
  return [...new Set(String(value || '').split(/[\s,，;；]+/).filter(Boolean))];
}
